import React, { useEffect, useState } from "react";
import "./login.css";
import login_img from "../../assets/image/password_change.jpeg";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { api } from "../../api/api";
import { useForm, Controller } from "react-hook-form";
import CountryCode from "../../Components/common/CountryCode/CountryCode";
import { toast } from "react-toastify";

const ForgotPassword = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const emailsFromURL = queryParams.get("email");

  const [usePhone, setUsePhone] = useState(!emailsFromURL); // phone first by default
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState("");

  const {
    register,
    handleSubmit,
    setValue,
    control,
    clearErrors,
    formState: { errors },
  } = useForm({
    defaultValues: {
      email: "",
      countryPhone: { countryCode: "+91", phone: "" },
    },
    mode: "onSubmit",
  });

  useEffect(() => {
    if (emailsFromURL) {
      setUsePhone(false);
      setValue("email", emailsFromURL);
    }
  }, [emailsFromURL, setValue]);

  const switchMode = (toPhone) => {
    if (toPhone === usePhone) return;
    setFormError("");
    clearErrors();
    setUsePhone(toPhone);
  };

  const onSubmit = async (data) => {
    setFormError("");
    try {
      setSubmitting(true);

      let payload = {};
      let nextQuery = "";

      if (usePhone) {
        const code = (data?.countryPhone?.countryCode || "").trim();
        const digits = String(data?.countryPhone?.phone || "").replace(/\D/g, "");

        if (!code) throw new Error("Please select a valid country code.");
        if (!digits) throw new Error("Phone number is required.");
        if (digits.length !== 10) {
          throw new Error("Please enter a valid 10-digit phone number.");
        }

        const fullPhone = code.startsWith("+")
          ? `${code}${digits}`
          : `+${code}${digits}`;

        payload = { phone: fullPhone };
        nextQuery = `phone=${encodeURIComponent(fullPhone)}`;
      } else {
        const email = String(data?.email || "").trim();
        if (!email) throw new Error("Please enter your email address.");
        payload = { email };
        nextQuery = `email=${encodeURIComponent(email)}`;
      }

      const res = await api(
        `api/v1/student/forgotPassword`,
        payload,
        "postWithoutToken",
        "",
        ""
      );

      const success =
        res?.success === true ||
        res?.status === true ||
        String(res?.status || "").toLowerCase() === "success";

      if (!success) {
        const msg =
          res?.errors?.[0]?.msg ||
          res?.errors?.[0]?.message ||
          res?.response ||
          res?.message ||
          res?.error ||
          "Request failed";
        throw new Error(msg);
      }

      navigate(`/reset-password?${nextQuery}`);
    } catch (e) {
      const msg = e?.message || "Something went wrong";
      setFormError(msg);
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="register-area login-area overflow-hidden">
      <div className="container">
        <div className="row">
          <div className="col-lg-6 mx-auto">
            <h3 className="text-center mb-4">
              Forgot Password?
              <span>
                {usePhone
                  ? "Enter your registered phone number to get an OTP."
                  : "Enter your registered email to get an OTP."}
              </span>
            </h3>
            <div className="img_sec img_showcase">
              <img src={login_img} alt="Forgot password" />
              <span className="img_chip chip_top" aria-hidden="true">
                <i className="fas fa-sms" /> OTP verification
              </span>
              <span className="img_chip chip_bottom" aria-hidden="true">
                <i className="fas fa-lock" /> Safe &amp; secure
              </span>
            </div>
            <div className="contact-form">
              <form className="mt-4" onSubmit={handleSubmit(onSubmit)} noValidate>
                <div className="row">
                  <div className="col-md-9 mx-auto">
                    {/* Phone / Email switch */}
                    <div className="login-switch" role="tablist" aria-label="Reset method">
                      <button
                        type="button"
                        role="tab"
                        aria-selected={usePhone}
                        className={usePhone ? "active" : ""}
                        onClick={() => switchMode(true)}
                      >
                        <i className="fas fa-mobile-alt" /> Phone Number
                      </button>
                      <button
                        type="button"
                        role="tab"
                        aria-selected={!usePhone}
                        className={!usePhone ? "active" : ""}
                        onClick={() => switchMode(false)}
                      >
                        <i className="fas fa-envelope" /> Email
                      </button>
                    </div>

                    {usePhone ? (
                      <div className="mb-3 px-2">
                        <Controller
                          name="countryPhone"
                          control={control}
                          rules={{
                            validate: (value) => {
                              if (!usePhone) return true;
                              const code = (value?.countryCode || "").trim();
                              const digits = String(value?.phone || "").replace(
                                /\D/g,
                                ""
                              );
                              if (!code) return "Country code is required";
                              if (!digits) return "Phone number is required";
                              if (digits.length !== 10)
                                return "Enter a valid 10-digit phone number";
                              return true;
                            },
                          }}
                          render={({ field }) => (
                            <CountryCode
                              label=""
                              placeholder="*Phone Number"
                              value={field.value}
                              onChange={field.onChange}
                            />
                          )}
                        />
                        {errors.countryPhone && (
                          <div
                            className="text-danger mt-1"
                            style={{ fontSize: "14px" }}
                          >
                            {errors.countryPhone.message}
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="mb-3 px-2">
                        <input
                          type="email"
                          className="form-control"
                          placeholder="*Email"
                          autoComplete="email"
                          {...register("email", {
                            validate: (value) => {
                              if (usePhone) return true;
                              if (!value || !String(value).trim()) {
                                return "Please enter your email address.";
                              }
                              const ok = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(
                                String(value).trim()
                              );
                              return ok || "Enter a valid email address.";
                            },
                          })}
                        />
                        {errors.email && (
                          <div
                            className="text-danger mt-1"
                            style={{ fontSize: "14px" }}
                          >
                            {errors.email.message}
                          </div>
                        )}
                      </div>
                    )}

                    {formError && (
                      <div
                        className="alert alert-danger"
                        role="alert"
                        style={{ fontSize: 14 }}
                      >
                        {formError}
                      </div>
                    )}

                    <button
                      className="btn_secondary btn_md w-100 mt-4 text-center"
                      type="submit"
                      disabled={submitting}
                    >
                      {submitting && (
                        <span className="spinner-border spinner-border-sm me-2" aria-hidden="true" />
                      )}
                      {submitting ? "Sending OTP..." : "Send OTP"}
                    </button>

                    <p className="already mt-3">
                      Remember your password?
                      <Link to="/login" className="ms-2">
                        Login
                      </Link>
                    </p>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
